import { useState } from "react";
import * as THREE from "three";
import { useSceneStore } from "../store/sceneStore";
import { getGeometry } from "../lib/loaders";
import { getScaledSize, getLocalSize } from "../lib/dimensions";
import { exportMesh } from "../lib/exporters";
import { ShareBox } from "./ShareBox";
import { CubeOutlineIcon } from "./icons";
import { commitUndo, pauseUndo } from "../lib/undo";
import type { Vec3 } from "../types";

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

export function PropertiesPanel() {
  const selectedId = useSceneStore((s) => s.selectedId);
  const objects = useSceneStore((s) => s.objects);
  const updateObject = useSceneStore((s) => s.updateObject);
  const selected = objects.find((o) => o.id === selectedId);

  const [targetAxis, setTargetAxis] = useState<0 | 1 | 2>(0);
  const [targetSize, setTargetSize] = useState<string>("");
  const [uniform, setUniform] = useState(true);

  if (!selected) {
    return (
      <div className="panel panel-right">
        <div className="empty-state">
          <CubeOutlineIcon />
          <p>Kein Objekt ausgewählt</p>
          <span>Wähle ein Objekt in der Szene oder der Liste links aus</span>
        </div>
      </div>
    );
  }

  const size = getScaledSize(selected);

  const setVec3 = (key: "position" | "rotation" | "scale", axis: 0 | 1 | 2, value: number) => {
    const next = [...selected[key]] as Vec3;
    next[axis] = value;
    updateObject(selected.id, { [key]: next });
  };

  const applyTargetSize = () => {
    const target = parseFloat(targetSize);
    if (!target || target <= 0) return;
    const local = getLocalSize(selected);
    const localValues = [local.x, local.y, local.z];
    const currentLocal = localValues[targetAxis] || 1;
    const factor = target / (currentLocal * selected.scale[targetAxis]);
    const newScale: Vec3 = uniform
      ? [selected.scale[0] * factor, selected.scale[1] * factor, selected.scale[2] * factor]
      : [...selected.scale];
    if (!uniform) newScale[targetAxis] = selected.scale[targetAxis] * factor;
    updateObject(selected.id, { scale: newScale });
  };

  const handleExport = (format: "stl" | "obj") => {
    const geometry = getGeometry(selected.id, selected.fileBase64, selected.format);
    const mesh = new THREE.Mesh(geometry);
    mesh.position.set(...selected.position);
    mesh.rotation.set(...selected.rotation);
    mesh.scale.set(...selected.scale);
    exportMesh(mesh, selected.name, format);
  };

  return (
    <div className="panel panel-right visible">
      <div className="section">
        <h2>Objekt</h2>
        <input
          type="text"
          value={selected.name}
          onFocus={pauseUndo}
          onBlur={commitUndo}
          onChange={(e) => updateObject(selected.id, { name: e.target.value })}
        />
      </div>

      <div className="section">
        <h2>Maße</h2>
        <div className="dim-readout">
          {(["X", "Y", "Z"] as const).map((axis, i) => (
            <div key={axis} className="lcd-cell">
              <span className="lcd-axis">{axis}</span>
              <span className="lcd-value">{size[i].toFixed(1)}</span>
              <span className="lcd-unit">mm</span>
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <h2>Auf Zielgröße skalieren</h2>
        <div className="field-row">
          <select value={targetAxis} onChange={(e) => setTargetAxis(Number(e.target.value) as 0 | 1 | 2)}>
            <option value={0}>X</option>
            <option value={1}>Y</option>
            <option value={2}>Z</option>
          </select>
          <input
            type="number"
            placeholder="mm"
            value={targetSize}
            onChange={(e) => setTargetSize(e.target.value)}
          />
          <button onClick={applyTargetSize}>Skalieren</button>
        </div>
        <label>
          <input type="checkbox" checked={uniform} onChange={(e) => setUniform(e.target.checked)} /> Gleichmäßig
          skalieren
        </label>
      </div>

      <div className="section">
        <h2>Position</h2>
        <Vec3Fields values={selected.position} onChange={(axis, v) => setVec3("position", axis, v)} />
      </div>

      <div className="section">
        <h2>Rotation (°)</h2>
        <Vec3Fields
          values={selected.rotation.map((r) => r * RAD2DEG) as Vec3}
          onChange={(axis, v) => setVec3("rotation", axis, v * DEG2RAD)}
        />
      </div>

      <div className="section">
        <h2>Skalierung</h2>
        <Vec3Fields values={selected.scale} step={0.01} onChange={(axis, v) => setVec3("scale", axis, v)} />
      </div>

      <div className="section">
        <h2>Material</h2>
        <div className="color-row">
          <input
            type="color"
            value={selected.color}
            onPointerDown={pauseUndo}
            onBlur={commitUndo}
            onChange={(e) => updateObject(selected.id, { color: e.target.value })}
          />
          <span>{selected.color}</span>
        </div>
        <label>Rauheit</label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={selected.roughness}
          onPointerDown={pauseUndo}
          onBlur={commitUndo}
          onChange={(e) => updateObject(selected.id, { roughness: Number(e.target.value) })}
        />
        <label>Metallic</label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={selected.metalness}
          onPointerDown={pauseUndo}
          onBlur={commitUndo}
          onChange={(e) => updateObject(selected.id, { metalness: Number(e.target.value) })}
        />
      </div>

      <div className="section">
        <h2>Export</h2>
        <div className="field-row">
          <button onClick={() => handleExport("stl")}>STL exportieren</button>
          <button onClick={() => handleExport("obj")}>OBJ exportieren</button>
        </div>
      </div>

      <div className="section">
        <h2>Teilen</h2>
        <ShareBox />
      </div>
    </div>
  );
}

function Vec3Fields({
  values,
  onChange,
  step = 1,
}: {
  values: Vec3;
  onChange: (axis: 0 | 1 | 2, value: number) => void;
  step?: number;
}) {
  return (
    <div className="field-grid">
      {(["X", "Y", "Z"] as const).map((label, axis) => (
        <div key={label} className="field-row">
          <label>{label}</label>
          <input
            type="number"
            step={step}
            value={Number(values[axis as 0 | 1 | 2].toFixed(3))}
            onFocus={pauseUndo}
            onBlur={commitUndo}
            onChange={(e) => onChange(axis as 0 | 1 | 2, Number(e.target.value))}
          />
        </div>
      ))}
    </div>
  );
}
